import React, { useState } from "react";
import { ActivityType, ItineraryActivity } from "../models/activity.model";
import { ModalCallbacks } from "../models/activities-modal.model";
import { getActivityTypeIcon } from "../models/activity-configs.model";
import AccommodationModal from "../modals/AccommodationModal";
import ActivityModal from "../modals/ActivityModal";
import FlightModal from "../modals/FlightModal";
import TransportationModal from "../modals/TransportationModal";
import FoodDrinksModal from "../modals/FoodDrinksModal";
import EventsModal from "../modals/EventsModal";
import NotesModal from "../modals/NotesModal";
import { RateHawkAccommodationModal } from "../modals/RateHawkAccommodationModal";
import { createCheckInOutActivities } from "@/components/utils/createCheckOutFromCheckIn";
import type { IGuest } from "@/lib/request-trip/models/travel-request.model";
import type { DayAxis } from "../models/day-axis";

interface UseModalCallbacksProps {
  onSave: (activity: ItineraryActivity, isEditing?: boolean, editingActivityId?: string) => void;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  /** Asse dei giorni: decide se le modali chiedono date reali o "Giorno N". */
  axis?: DayAxis | null;
  itineraryId: string;
  destinations?: string[];
  guestsData?: IGuest[];
  totalGuests?: number;
  /** Provenienza del viaggiatore dalla travel request, ISO alpha-2 minuscolo. */
  residency?: string;
  selectedDate?: string;
}

export const useModalCallbacks = ({ onSave, itineraryStartDate, itineraryEndDate, axis, itineraryId, destinations, guestsData, totalGuests, residency, selectedDate }: UseModalCallbacksProps): ModalCallbacks => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState<ActivityType | null>(null);
  const [editingActivity, setEditingActivity] = useState<ItineraryActivity | null>(null);

  // La ricerca sul portale e' un flusso a se': ci si arriva dalla scorciatoia
  // "Cerca sul portale" dentro la modale alloggio.
  const [isRateHawkModalOpen, setIsRateHawkModalOpen] = useState(false);

  const openModal = (type: ActivityType, icon?: React.ReactNode, activityData?: ItineraryActivity) => {
    setSelectedActivityType(type);
    setEditingActivity(activityData || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedActivityType(null);
    setEditingActivity(null);
  };

  const handleRateHawkSave = (activityData: Partial<ItineraryActivity>) => {
    setIsRateHawkModalOpen(false);
    onSave(activityData as ItineraryActivity, false, undefined);
  };

  const handleSave = (activityData: Partial<ItineraryActivity>) => {
    const isEditing = editingActivity !== null;
    const editingActivityId = isEditing ? editingActivity?.id : undefined;

    onSave(activityData as ItineraryActivity, isEditing, editingActivityId);
  };

  const renderModal = (): React.ReactNode => {
    const modals: React.ReactNode[] = [];

    // RateHawk modal
    modals.push(
      React.createElement(RateHawkAccommodationModal, {
        key: 'ratehawk-accommodation',
        isOpen: isRateHawkModalOpen,
        onClose: () => setIsRateHawkModalOpen(false),
        onSave: handleRateHawkSave,
        destinations,
        startDate: itineraryStartDate,
        endDate: itineraryEndDate,
        axis,
        currentDayDate: selectedDate,
        guestsData,
        totalGuests,
        residency,
      })
    );

    // Standard activity modals
    if (!selectedActivityType) return React.createElement(React.Fragment, null, ...modals);

    const icon = getActivityTypeIcon(selectedActivityType);

    const commonProps = {
      isOpen: isModalOpen,
      onClose: closeModal,
      onSave: handleSave,
      icon,
      itineraryStartDate,
      itineraryEndDate,
      axis,
      itineraryId,
      defaultDate: selectedDate || undefined
    };

    let activityModal: React.ReactNode = null;
    switch (selectedActivityType) {
      case 'accommodation':
        activityModal = React.createElement(AccommodationModal, {
          ...commonProps,
          initialData: (editingActivity?.type.startsWith('accommodation')) ? editingActivity : undefined,
          guestsData,
          totalGuests,
          residency,
          // Scorciatoia dalla modale alla ricerca sul portale: chiude questa e apre quella.
          onSearchPortal: () => {
            closeModal();
            setIsRateHawkModalOpen(true);
          },
        });
        break;
      case 'activity':
        activityModal = React.createElement(ActivityModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'activity' ? editingActivity : undefined
        });
        break;
      case 'flight':
        activityModal = React.createElement(FlightModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'flight' ? editingActivity : undefined
        });
        break;
      case 'transportation':
        activityModal = React.createElement(TransportationModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'transportation' ? editingActivity : undefined
        });
        break;
      case 'food-drinks':
        activityModal = React.createElement(FoodDrinksModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'food-drinks' ? editingActivity : undefined
        });
        break;
      case 'events':
        activityModal = React.createElement(EventsModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'events' ? editingActivity : undefined
        });
        break;
      case 'notes':
        activityModal = React.createElement(NotesModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'notes' ? editingActivity : undefined
        });
        break;
    }

    modals.push(activityModal);
    return React.createElement(React.Fragment, null, ...modals);
  };

  return {
    openModal,
    closeModal,
    renderModal,
    isModalOpen: isModalOpen || isRateHawkModalOpen,
    selectedActivityType
  };
};
